import 'chartjs-adapter-moment';
import { useMemo, useState } from 'react';
import { Button, ButtonGroup, Col, Container, Form, Row } from "react-bootstrap";
import { Line } from 'react-chartjs-2';
import { getPacificDateString } from '../../utils/time';
import './Compass.css';
import { getEQ5DScore } from './VitalSignStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

export const CompassTrends = (props: {
    user: any,
    vitalTrendKeys: string[],
    dailyKeyToData: any,
    compassDates: any,
    dateToSurveyAvailability: any,
    metricToTrends: any,
    isLoadingMetricToTrends: boolean,
}) => {

    const [showVitalsTrends, setShowVitalsTrends] = useState<boolean>(true);
    const [showVitalsDay, setShowVitalsDay] = useState<boolean>(true);
    const [showVitalsNight, setShowVitalsNight] = useState<boolean>(false);
    const [showVitalsWake, setShowVitalsWake] = useState<boolean>(false);
    const [showVitalsSleep, setShowVitalsSleep] = useState<boolean>(false);
    const [showVitalsActive, setShowVitalsActive] = useState<boolean>(false);
    const [showVitalsResting, setShowVitalsResting] = useState<boolean>(true);
    const [showVitalsMin, setShowVitalsMin] = useState<boolean>(false);
    const [showVitalsAvg, setShowVitalsAvg] = useState<boolean>(true);
    const [showVitalsMax, setShowVitalsMax] = useState<boolean>(false);

    const [selectedSurveyMetrics, setSelectedSurveyMetrics] = useState<string[]>(['EQ5D']);
    const [selectedVitals, setSelectedVitals] = useState<string[]>(['PRV', 'PULSE_RATE']);

    const getVitalRoot = (v: string) => {
        if (v.endsWith("MIN") || v.endsWith("AVG") || v.endsWith("MAX")) {
            // e.g. v = ACTIVITY_COUNTS_RESTING_NIGHTTIME_MIN
            return v.split(/_(?=[^_]*_[^_]*_[^_]*$)/)[0];
        } else {
            return v;
        }
    };

    const uniqueVitals = useMemo(() => {
        const uniqueVitalRoots = new Set<string>();
        for (let v of props.vitalTrendKeys) {
            uniqueVitalRoots.add(getVitalRoot(v));
        }
        return Array.from(uniqueVitalRoots).sort();
    }, [props.vitalTrendKeys]);

    const setShowVitalsTimePeriod = (period: string) => {
        let day = false;
        let night = false;
        let wake = false;
        let sleep = false;
        if (period === "day") {
            day = true;
        } else if (period === "night") {
            night = true;
        } else if (period === "wake") {
            wake = true;
        } else if (period === "sleep") {
            sleep = true;
        }
        setShowVitalsDay(day);
        setShowVitalsNight(night);
        setShowVitalsWake(wake);
        setShowVitalsSleep(sleep);
    }

    const onChangeSurveyMetrics = (type: string) => {
        let metrics = [...selectedSurveyMetrics];
        if (metrics.includes(type)) {
            metrics = metrics.filter((x) => x !== type);
        } else {
            metrics.push(type);
        }
        setSelectedSurveyMetrics(metrics);
    };

    const onChangeVital = (type: string) => {
        let newVitals = [...selectedVitals];
        if (newVitals.includes(type)) {
            newVitals = newVitals.filter((x) => x !== type);
        } else {
            newVitals.push(type);
        }
        setSelectedVitals(newVitals);
    };

    const getNumericsChartData = (compassDates: Date[], data: any) => {
        return {
            datasets: [{
                data: compassDates.map((dt, idx) => {
                    let dtStr = getPacificDateString(dt);
                    return {
                        x: idx,
                        y: data[dtStr],
                    };
                }),
                lineTension: 0,
                spanGaps: false,
                fill: 'origin',
            }]
        };
    };

    const getSurveyChartData = (compassDates: Date[], data: any) => {
        return {
            datasets: [{
                data: compassDates.map((dt, idx) => {
                    let dtStr = getPacificDateString(dt);
                    return {
                        x: idx,
                        y: getEQ5DScore(data, dtStr),
                    };
                }),
                lineTension: 0,
                spanGaps: false,
                fill: 'origin',
            }]
        };
    };

    const baseChartOptions = {
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'linear',
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(107, 114, 128, 0.1)'
                },
            }
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                mode: 'index' as const,
                intersect: false
            }
        },
        interaction: {
            mode: 'index' as const,
            intersect: false
        }
    }

    const getNumericsChartOptions = (): any => {
        return {
            ...baseChartOptions,
            scales: {
                ...baseChartOptions.scales,
                y: {
                    beginAtZero: true,
                    grid: {
                        color: 'rgba(107, 114, 128, 0.1)'
                    },
                    ticks: {
                        color: '#6b7280',
                    },
                }
            },
            plugins: {
                ...baseChartOptions.plugins,
                tooltip: {
                    ...baseChartOptions.plugins.tooltip,
                    mode: 'nearest',
                    callbacks: {
                        label: (context: any) => context.raw.y,
                        title: (tooltipItems: any[]) => {
                            if (tooltipItems[0]) {
                                return `Day ${tooltipItems[0].parsed.x}`
                            }
                            return '';
                        }
                    }
                }
            },
            interaction: {
                ...baseChartOptions.interaction,
                mode: 'nearest'
            }
        };
    }

    return (
        <div>
            <Container fluid>
                <Row>
                    <Col md={3} style={{ borderRight: '1px solid #999999' }}>
                        <Row className="mt-2">
                            <Col md={12}>
                                <div>
                                    <strong>Time Period</strong>
                                </div>
                                <ButtonGroup size='sm'>
                                    <Button variant={showVitalsDay ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsTimePeriod('day'); }}>Daytime</Button>
                                    <Button variant={showVitalsNight ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsTimePeriod('night'); }}>Nighttime</Button>
                                    <Button variant={showVitalsWake ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsTimePeriod('wake'); }}>Wake</Button>
                                    <Button variant={showVitalsSleep ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsTimePeriod('sleep'); }}>Sleep</Button>
                                </ButtonGroup>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <div>
                                    <strong>Activity</strong>
                                </div>
                                <ButtonGroup size='sm'>
                                    {/* <Button variant={showVitalsActive && showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsActive(true); setShowVitalsResting(true); }}>All</Button> */}
                                    <Button variant={showVitalsActive && !showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsActive(true); setShowVitalsResting(false); }}>Active</Button>
                                    <Button variant={!showVitalsActive && showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsActive(false); setShowVitalsResting(true); }}>Resting</Button>
                                </ButtonGroup>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <div>
                                    <strong>Statistic</strong>
                                </div>
                                <ButtonGroup size='sm'>
                                    {/* <Button variant={showVitalsMin && showVitalsAvg && showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsMin(true); setShowVitalsAvg(true); setShowVitalsMax(true); }}>All</Button> */}
                                    <Button variant={showVitalsMin && !showVitalsAvg && !showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsMin(true); setShowVitalsAvg(false); setShowVitalsMax(false); }}>Min</Button>
                                    <Button variant={!showVitalsMin && showVitalsAvg && !showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsMin(false); setShowVitalsAvg(true); setShowVitalsMax(false); }}>Average</Button>
                                    <Button variant={!showVitalsMin && !showVitalsAvg && showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => { setShowVitalsMin(false); setShowVitalsAvg(false); setShowVitalsMax(true); }}>Max</Button>
                                </ButtonGroup>
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col md={12}>
                                <div>
                                    <strong>Displayed Surveys</strong>
                                </div>

                                {/* {JSON.stringify(props.dateToSurveyAvailability)} */}
                                <div className="d-flex">
                                    <Form>
                                        <Form.Check
                                            type='checkbox'
                                            label='EQ5D'
                                            checked={selectedSurveyMetrics.includes('EQ5D')}
                                            onChange={() => onChangeSurveyMetrics('EQ5D')}
                                        />
                                    </Form>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mt-2 mb-4">
                            <Col md={12}>
                                <strong>Displayed Charts</strong>

                                <div className="d-flex">
                                    <Form>
                                        {uniqueVitals.map((type) => (
                                            <div key={`default-${type}`}>
                                                <Form.Check
                                                    type='checkbox'
                                                    id={`${type}`}
                                                    label={`${type}`}
                                                    checked={selectedVitals.includes(type)}
                                                    onChange={() => onChangeVital(type)}
                                                />
                                            </div>
                                        ))}
                                    </Form>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={9}>
                        {
                            selectedSurveyMetrics.map((k) => {
                                return (
                                    <>
                                        <h6>{k}</h6>
                                        <Row style={{maxHeight: '200px'}}>
                                            <Line
                                                key={k}
                                                data={getSurveyChartData(props.compassDates, props.dateToSurveyAvailability)}
                                                options={getNumericsChartOptions()}
                                            />
                                        </Row>
                                        <hr />
                                    </>
                                )
                            })
                        }
                        {
                            showVitalsTrends && props.vitalTrendKeys.filter((key: string) => {
                                if (!selectedVitals.includes(getVitalRoot(key))) {
                                    return false;
                                }
                                if (!showVitalsDay && key.includes("_DAYTIME")) {
                                    return false;
                                }
                                if (!showVitalsNight && key.includes("_NIGHTTIME")) {
                                    return false;
                                }
                                if (!showVitalsWake && key.includes("_WAKE")) {
                                    return false;
                                }
                                if (!showVitalsSleep && key.includes("_SLEEP") && key !== "TOTAL_SLEEP_TIME") {
                                    return false;
                                }
                                if (!showVitalsActive && key.includes("_ACTIVE")) {
                                    return false;
                                }
                                if (!showVitalsResting && key.includes("_RESTING")) {
                                    return false;
                                }
                                if (!showVitalsMin && key.includes("_MIN")) {
                                    return false;
                                }
                                if (!showVitalsAvg && key.includes("_AVG")) {
                                    return false;
                                }
                                if (!showVitalsMax && key.includes("_MAX")) {
                                    return false;
                                }
                                return true;
                            }).map((k) => {
                                return (
                                    <>
                                        <h6>{k}</h6>
                                        {
                                            props.isLoadingMetricToTrends ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</>
                                            ) : props.metricToTrends[k] ? (
                                                <p>
                                                    <strong>Overall Slope</strong>: {props.metricToTrends[k]["overall_slope"]} &bull; <strong>R Squared</strong>: {props.metricToTrends[k]["r_squared"]} &bull; <strong>p-value</strong>: {props.metricToTrends[k]["p_value"]} &bull; <strong>Total Change</strong>: {props.metricToTrends[k]["total_change"]} &bull; <strong>% Change</strong>: {props.metricToTrends[k]["percent_change"]} &bull; <strong>Volatility</strong>: {props.metricToTrends[k]["volatility"]} &bull; <strong>Pre Revisit Slope</strong>: {props.metricToTrends[k]["pre_revisit_slope"]} &bull; <strong>Pre Revisit p-value</strong>: {props.metricToTrends[k]["pre_revisit_p_value"]} &bull; <strong>Last 5d Change</strong>: {props.metricToTrends[k]["last_5d_change"]}
                                                </p>
                                            ) : (
                                                <></>
                                            )
                                        }
                                        {/* <p>{JSON.stringify(props.dailyKeyToData[k])}</p>
                                        <p>{JSON.stringify(getNumericsChartData(props.compassDates, props.dailyKeyToData[k]))}</p> */}
                                        <Row style={{maxHeight: '200px'}}>
                                            <Line
                                                key={k}
                                                data={getNumericsChartData(props.compassDates, props.dailyKeyToData[k])}
                                                options={getNumericsChartOptions()}
                                            />
                                        </Row>
                                        <hr />
                                    </>
                                )
                            })
                        }
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
